import { Component, OnInit } from '@angular/core';
import { VenueDetailsService } from '../../services/venue-details.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'src/app/services/notifier.service';
import { APIService } from 'src/app/services/api.service';
import { getENV } from 'src/app/_helpers/helpers';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Interface for HR Exporter Connector Group
interface HRExporterConnectorGroup {
  id?: string;
  name: string;
  description: string;
  connectors: any[];
  isNew?: boolean;
  isUpdated?: boolean;
  isDeleted?: boolean;

  // API response fields
  Id?: number;
  Name?: string;
  Description?: string;
}

interface ConfirmModalData {
  title: string;
  message: string;
  confirmText?: string;
  onConfirm: () => void;
}

@Component({
  selector: 'app-connector-management',
  templateUrl: './connector-management.component.html',
  styleUrls: ['./connector-management.component.css'],
})
export class ConnectorManagementComponent implements OnInit {
  // Add loading state properties
  isLoadingGroups = false;
  isSaving = false;

  // Properties
  config: any;
  connectorGroups: HRExporterConnectorGroup[] = [];
  selectedGroupId: string = '';
  selectedGroup: HRExporterConnectorGroup | null = null;

  // Form groups
  connectorGroupForm: FormGroup;
  // Modal control
  showGroupModal = false;
  showConfirmModal = false;
  editMode = false;

  // Add new properties for confirmation modal
  confirmModalData: ConfirmModalData = {
    title: '',
    message: '',
    onConfirm: () => { }
  };

  constructor(
    private venueDetailsService: VenueDetailsService,
    private route: ActivatedRoute,
    private router: Router,
    private notifier: NotifierService,
    private apiService: APIService,
    private fb: FormBuilder
  ) {
    this.connectorGroupForm = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });

    this.config = this.route.snapshot.data['config'];
  }

  ngOnInit(): void {
    // Initialize modals as hidden
    this.showGroupModal = false;

    // Load connector groups
    this.loadConnectorGroups();
  }  // Load connector groups from the server
  async loadConnectorGroups(): Promise<void> {
    this.isLoadingGroups = true;
    try {
      const response = await this.venueDetailsService.getConnectorGroups(this.config);
      if (response && response.data) {
        // Handle the API response format
        this.connectorGroups = response.data.map((group: any) => ({
          id: group.Id?.toString(),
          name: group.Name,
          description: group.Description,
          Id: group.Id,
          Name: group.Name,
          Description: group.Description,
          connectors: []
        }));

        console.log('Loaded connector groups:', this.connectorGroups);

        // Set the first group as selected if available
      }
    } catch (err) {
      this.notifier.alert('Error', '', 'Failed to load connector groups.', 'error');
      console.error('Error loading connector groups:', err);
    } finally {
      this.isLoadingGroups = false;
    }
  }
  // Handle group selection
  onGroupSelect(groupId: string): void {
    // Navigate to the connector group management page
    this.router.navigate(['/dash/connector-management', groupId]);
  }
  // Open connector group modal
  openGroupModal(event: any, group?: HRExporterConnectorGroup): void {
    event.stopPropagation();
    this.editMode = !!group;

    if (group) {
      this.connectorGroupForm.setValue({
        name: group.name || group.Name || '',
        description: group.description || group.Description || ''
      });
      // Store the selected group ID for editing
      this.selectedGroupId = group.id || (group.Id?.toString()) || '';
    } else {
      this.connectorGroupForm.reset({
        name: '',
        description: ''
      });
    }

    this.showGroupModal = true;
  }

  // Close connector group modal
  closeGroupModal(): void {
    this.showGroupModal = false;
  }
  // Save connector group
  async saveConnectorGroup(): Promise<void> {
    if (this.connectorGroupForm.invalid) {
      this.notifier.alert('Error', '', 'Please fill in all required fields.', 'error');
      return;
    }

    const action = this.editMode ? 'update' : 'create';
    this.openConfirmModal({
      title: `${this.editMode ? 'Update' : 'Create'} Connector Group`,
      message: `Are you sure you want to ${action} this connector group?`,
      confirmText: this.editMode ? 'Update' : 'Create',
      onConfirm: async () => {
        this.isSaving = true;
        try {
          const formValue = this.connectorGroupForm.value;
          const payload: any = {
            action: this.editMode ? 'update_connector_group' : 'create_connector_group',
            data: {
              name: formValue.name,
              description: formValue.description
            }
          };

          if (this.editMode && this.selectedGroupId) {
            payload.data.id = this.selectedGroupId;
          }

          await this.venueDetailsService.processConnectorGroup(this.config, payload);
          this.notifier.alert('Success', '', `Connector group ${this.editMode ? 'updated' : 'created'} successfully.`, 'success');
          this.closeGroupModal();
          this.loadConnectorGroups();
        } catch (err) {
          this.notifier.alert('Error', '', `Failed to ${this.editMode ? 'update' : 'create'} connector group.`, 'error');
          console.error(`Error ${this.editMode ? 'updating' : 'creating'} connector group:`, err);
        } finally {
          this.isSaving = false;
          this.closeConfirmModal();
        }
      }
    });
  }

  // Delete connector group
  async deleteConnectorGroup(event: any, groupId: string): Promise<void> {
    event.stopPropagation();
    this.openConfirmModal({
      title: 'Delete Connector Group',
      message: 'Are you sure you want to delete this connector group? This will also delete all associated connectors.',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await this.venueDetailsService.deleteConnectorGroup(this.config, groupId);
          this.notifier.alert('Success', '', 'Connector group deleted successfully.', 'success');
          this.loadConnectorGroups();
        } catch (err) {
          this.notifier.alert('Error', '', 'Failed to delete connector group.', 'error');
          console.error('Error deleting connector group:', err);
        }
        this.closeConfirmModal();
      }
    });
  }

  // Add confirmation modal methods
  openConfirmModal(data: ConfirmModalData): void {
    this.confirmModalData = data;
    this.showConfirmModal = true;
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }
}
